import React, { useEffect, useMemo, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";

import { CompositeField } from "objs/_ConfiguratorObjs/Wizard/SubComponents/WizardQuestionnaire/Question/CompositeField/CompositeField";
import {
  getSplitByTypeFields,
  groupFieldsByFieldGroups,
} from "objs/_ConfiguratorObjs/Wizard/SubComponents/WizardQuestionnaire/questionnaireHelpers";
import { QuestionWrapper } from "components/QuestionWrapper";
import { QuestionnaireField } from "objs/_ConfiguratorObjs/Wizard/SubComponents/WizardQuestionnaire/Question/QuestionnaireField/QuestionnaireField";
import { useResizeDetector } from "react-resize-detector";
import { Alert } from "reactstrap";
import { useFormikContext } from "formik";
import { checkIsValid } from "objs/_ConfiguratorObjs/Wizard/SubComponents/WizardQuestionnaire/Question/questionValidationHelpers";
import {
  useWizardActions,
  useWizardQuestionsValidation,
} from "stores/wizardStore";

export const Question = Scrivito.connect(({ data }) => {
  const { fields, fieldGroups, identifier, constraints } = data;

  const { setWizardQuestionsValidation } = useWizardActions();

  const wizardQuestionsValidation = useWizardQuestionsValidation();

  const { values } = useFormikContext();

  const [validationMessages, setValidationMessages] = useState([]);

  const constraintsValidations = useMemo(() => {
    if (!constraints || constraints.length === 0 || !values) {
      return [];
    }

    return constraints.map((rule) => ({
      ...rule,
      scope: rule.scope.map((field) => {
        const fieldParts = field.split(".");

        const matchedKey = Object.keys(values).find((key) =>
          fieldParts.every((part) => key.includes(part)),
        );

        return (
          matchedKey || {
            [`${fieldParts[0]}/#radiobutton`]: `${fieldParts[0]}/#${field.replace(".", ",")}/#bool`,
          }
        );
      }),
    }));
  }, [values, constraints]);

  useEffect(() => {
    if (constraintsValidations && constraintsValidations?.length !== 0) {
      const { isValid, messages } = checkIsValid({
        constraints: constraintsValidations,
        values,
      });
      if (!isValid) {
        setValidationMessages(messages);
        setWizardQuestionsValidation({
          [identifier]: false,
        });
      } else {
        setValidationMessages([]);
        setWizardQuestionsValidation({
          [identifier]: true,
        });
      }
    }
  }, [
    values,
    constraintsValidations,
    setWizardQuestionsValidation,
    identifier,
  ]);

  const { width, ref } = useResizeDetector();

  const isTable = useMemo(
    () => fields[0].fieldInputType === "tableheader",
    [fields],
  );

  const splitFields = useMemo(() => getSplitByTypeFields(fields), [fields]);
  const groupedByGroupId = useMemo(
    () => groupFieldsByFieldGroups(fieldGroups, splitFields),
    [fieldGroups, splitFields],
  );

  return (
    <div ref={ref}>
      <QuestionWrapper data={data}>
        {isTable ? (
          <CompositeField data={fields} identifier={identifier} />
        ) : (
          groupedByGroupId?.map((fieldsGroup, groupKey) =>
            fieldsGroup?.fieldGroupId ? (
              <div
                key={groupKey}
                className={cx("d-flex flex-column w-100", {
                  "border-top mt-3 pt-3": groupKey !== 0,
                })}
              >
                {fieldsGroup?.title && (
                  <span className="mb-3 text-blue-800 text-uppercase font-md">
                    {fieldsGroup?.title}
                  </span>
                )}
                <div
                  className={cx("d-flex flex-wrap w-100 mx-2_5", {
                    "mb-3": groupKey !== 0,
                  })}
                >
                  {fieldsGroup.fields.map((field, key) => (
                    <QuestionnaireField
                      field={field}
                      key={field.fieldKey}
                      parentIdentifier={identifier}
                      index={key}
                      fieldsCount={fieldsGroup.length}
                      width={width}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div
                key={groupKey}
                className={cx("d-flex flex-wrap w-100  mx-2_5", {
                  "mt-2 mb-3": groupKey !== 0,
                })}
              >
                {fieldsGroup.map((field, key) => (
                  <QuestionnaireField
                    field={field}
                    key={field.fieldKey}
                    parentIdentifier={identifier}
                    index={key}
                    fieldsCount={fieldsGroup.length}
                    width={width}
                  />
                ))}
              </div>
            ),
          )
        )}

        {wizardQuestionsValidation?.isValidationMessageShown &&
          validationMessages.length !== 0 && (
            <div className="w-100">
              {validationMessages.map((message, key) => (
                <Alert key={key} color="warning" className="mt-3 w-100">
                  {message}
                </Alert>
              ))}
            </div>
          )}
      </QuestionWrapper>
    </div>
  );
});
